import React, { useState, useEffect } from "react";
import { styled } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Autocomplete, Button, Grid, Paper, Typography, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import TableN from "../../reusableComponents/components/generics/TableN";
import { fetchHealthFacilitiesWithPackage } from "../../action";
import Delete from "@mui/icons-material/Delete";
import _ from "lodash";
import CommonSnackbar from "../../reusableComponents/components/generics/CommonSnackbar";

const StyledPaper = styled(Paper)(({ theme }) => ({
  ...theme.paper.paper,
}));

const StyledPaperHeader = styled(Grid)(({ theme }) => ({
  ...theme.paper.header,
  fontSize: 600,
  padding: ".8rem",
}));

export const EXAMINATION_ACT_TYPE_LABEL = "examination";

function ClaimExaminationPannel({ onDataChange, claimData, disabled, nonRemovableExaminationActIds, preAuthData }) {
  const SignInSuccess = useSelector((state) => state);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [totalClaimed, setTotalClaimed] = useState(0);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const healthData = SignInSuccess?.fosaReducer?.pickerHealthFacilityInsurees?.[0];
  const healthFacilityUuid = claimData?.healthFacility?.uuid || healthData?.uuid;
  const healthSupport = SignInSuccess?.fosaReducer?.healthFacilityHealthSupport ?? 100;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  /* eslint-disable no-unused-vars -- kept for Dialog/handleClose consistency with ClaimActsPannel */
  const [popupMessage, setPopupMessage] = useState("");
  const [deleteType, setDeleteType] = useState(false);
  const [index, setIndex] = useState("");
  /* eslint-enable no-unused-vars */

  // New state to store the previous value before change
  const [prevRows, setPrevRows] = useState([]);

  useEffect(() => {
    if (healthFacilityUuid) {
      dispatch(fetchHealthFacilitiesWithPackage(healthFacilityUuid, ["first:50"]));
    }
  }, [dispatch, healthFacilityUuid]);

  useEffect(() => {
    if (SignInSuccess?.fosaReducer?.healthFacilitiesPackageData) {
      const acts = SignInSuccess.fosaReducer.healthFacilitiesPackageData;
      const examinationActs = (acts || []).filter(
        (item) => (item?.actM2m?.actType?.label || "").toLowerCase() === EXAMINATION_ACT_TYPE_LABEL.toLowerCase()
      );
      setFilteredServices(examinationActs);
    }
  }, [SignInSuccess?.fosaReducer?.healthFacilitiesPackageData]);

  useEffect(() => {
    if (claimData?.acts && claimData.acts.length > 0) {
      const examinationActs = claimData.acts.filter(
        (actItem) => (actItem?.act?.actType?.label || "").toLowerCase() === EXAMINATION_ACT_TYPE_LABEL.toLowerCase()
      );
      if (examinationActs.length > 0) {
        const initialRows = examinationActs.map((actItem) => {
          const jsonExt = JSON.parse(actItem.jsonExt || actItem.json_ext || "{}");
          const act = actItem.act;
          const fullCode = act?.fullCode || act?.full_code || "";
          return {
            actId: act?.uuid || act?.id,
            service: fullCode,
            price: actItem.priceAsked || act?.priceHomologated || act?.price_homologated,
            quantity: actItem.qtyProvided,
            priceAsked: actItem.priceAsked,
            countedPrice: jsonExt.countedPrice,
            tmValue: jsonExt.tmValue,
            explanation: actItem.explanation,
          };
        });
        setRows(initialRows);
        setPrevRows(initialRows);
        setSelectedServices(initialRows.map((row) => row.actId).filter(Boolean));
        calculateTotalClaimed(initialRows);
        onDataChange(initialRows);
      } else {
        const emptyRow = [
          {
            actId: "",
            service: "",
            quantity: "",
            price: "",
            tmValue: "",
            countedPrice: "",
            explanation: "",
            priceAsked: "",
          },
        ];
        setRows(emptyRow);
        onDataChange(emptyRow);
      }
    } else {
      const emptyRow = [
        {
          actId: "",
          service: "",
          quantity: "",
          price: "",
          tmValue: "",
          countedPrice: "",
          explanation: "",
          priceAsked: "",
        },
      ];
      setRows(emptyRow);
      onDataChange(emptyRow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onDataChange is stable from parent
  }, [claimData]);

  const handleServiceChange = (index, option) => {
    if (!option) return;
    setPrevRows(rows);

    if (option?.actM2m?.apStatus === 'REQUIS' && !preAuthData?.paActs) {
      setSnackbarOpen(true);
      setSnackbarSeverity("error");
      setSnackbarMessage("cet acte nécessite un accord préalable");
      return;
    }

    const selectedAct = filteredServices.find(
      (item) => (item?.actM2m?.uuid || item?.actM2m?.id) === option.id
    );
    if (!selectedAct?.actM2m) return;

    const actM2m = selectedAct.actM2m;
    const price = Number(selectedAct.agreedPrice ?? actM2m.priceHomologated ?? 0);
    const healthSupportPct = Number(healthSupport) || 100;
    const countedPricePerUnit = _.round(price * (healthSupportPct / 100), 2);
    const tmValuePerUnit = _.round(price * ((100 - healthSupportPct) / 100), 2);

    const fullCode = actM2m.fullCode || actM2m.full_code || "";

    const newRows = rows.map((row, i) =>
      i === index
        ? {
          ...row,
          actId: actM2m.uuid || actM2m.id,
          service: fullCode,
          price,
          quantity: 1,
          priceAsked: price,
          countedPrice: String(countedPricePerUnit),
          tmValue: String(tmValuePerUnit),
        }
        : row
    );
    setRows(newRows);
    onDataChange(newRows);
    const newSelectedServices = newRows
      .map((row) => row.actId)
      .filter((s) => s !== "");
    setSelectedServices(newSelectedServices);
    calculateTotalClaimed(newRows);
  };

  const handleChange = (index, event) => {
    setPrevRows(rows);
    const { name, value } = event.target;
    const newRows = rows.map((row, i) =>
      i === index ? { ...row, [name]: value } : row
    );
    setRows(newRows);
    onDataChange(newRows);
    if (name === "quantity") {
      calculateTotalClaimed(newRows);
    }
  };

  const addRow = () => {
    setRows([
      ...rows,
      {
        actId: "",
        service: "",
        quantity: "",
        price: "",
        tmValue: "",
        countedPrice: "",
        explanation: "",
        priceAsked: "",
      },
    ]);
  };

  const deleteRow = (index) => {
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
    const newSelectedServices = newRows
      .map((row) => row.actId)
      .filter((id) => id !== "");
    setSelectedServices(newSelectedServices);
    calculateTotalClaimed(newRows);
    onDataChange(newRows);
  };

  const getActOption = (item) => {
    const actM2m = item?.actM2m;
    const fullCode = actM2m?.fullCode || actM2m?.full_code || "";
    return {
      ...item,
      label: fullCode,
      id: actM2m?.uuid || actM2m?.id,
    };
  };

  const getAvailableActs = (currentActId) => {
    return filteredServices.filter(
      (item) =>
        !selectedServices.includes(item?.actM2m?.uuid || item?.actM2m?.id) ||
        (item?.actM2m?.uuid || item?.actM2m?.id) === currentActId
    );
  };

  const headers = () => {
    return [
      t("Service"),
      t("Quantity"),
      t("Price"),
      t("TicketModerateur"),
      t("CountedPrice"),
      t("Explanation"),
      t("Action"),
    ];
  };

  const calculateTotalClaimed = (rows) => {
    const total = rows.reduce((sum, row) => sum + row.quantity * row.price, 0);
    setTotalClaimed(total);
  };

  const totalTmValue = _.round(
    rows.reduce((sum, r) => {
      const quantity = r.quantity || 1;
      const tmValue = r.tmValue ? r.tmValue : null;
      return sum + (tmValue ? parseFloat(tmValue) * quantity : 0);
    }, 0),
    2
  );

  const totalCounterPriceValue = _.round(
    rows.reduce((sum, r) => {
      const quantity = r.quantity || 1;
      const countedPrice = r.countedPrice ? r.countedPrice : null;
      return sum + (countedPrice ? parseFloat(countedPrice) * quantity : 0);
    }, 0),
    2
  );

  const multiplyVal = (value, multiplyBy) => {
    return parseFloat(value) * parseFloat(multiplyBy);
  };

  const itemFormatters = () => {
    return [
      (row, index) => {
        const availableOptions = getAvailableActs(row.actId).map(getActOption);
        const selectedOption = row.actId
          ? availableOptions.find((opt) => String(opt?.id) === String(row.actId))
          : null;
        // When loading from fetchClaimById, the act may not be in package options; show row.service (fullCode) anyway
        const displayValue = selectedOption ?? (row.actId && row.service ? { id: row.actId, label: row.service } : null);
        return (
          <Autocomplete
            options={availableOptions}
            getOptionLabel={(opt) => opt?.label ?? ""}
            isOptionEqualToValue={(opt, val) => opt?.id != null && val?.id != null && String(opt.id) === String(val.id)}
            sx={{ minWidth: 300, width: "100%" }}
            size="small"
            value={displayValue}
            disabled={disabled}
            onChange={(e, option) => {
              handleServiceChange(index, option);
            }}
            renderInput={(params) => <TextField {...params} />}
          />
        );
      },
      (row, index) => (
        <TextField
          type="number"
          className="no-arrow"
          name="quantity"
          size="small"
          value={row.quantity ? row.quantity : ""}
          onChange={(e) => handleChange(index, e)}
          disabled={disabled}
        />
      ),
      (row) => (
        <TextField
          type="text"
          name="price"
          value={row.price != null && row.price !== "" ? _.round(Number(row.price) * (Number(row.quantity) || 1), 2) : ""}
          size="small"
          disabled={true}
          onChange={(e) => handleChange(row, e, "price")}
        />
      ),
      (row) => (
        <TextField
          type="text"
          name="ticket"
          size="small"
          value={row.tmValue ? multiplyVal(row.tmValue, row.quantity) : ""}
          disabled={true}
          onChange={(e) => handleChange(row, e, "ticket")}
        />
      ),
      (row) => (
        <TextField
          type="text"
          name="countedPrice"
          size="small"
          value={row.countedPrice ? multiplyVal(row.countedPrice, row.quantity) : ""}
          disabled={true}
          onChange={(e) => handleChange(row, e, "countedPrice")}
        />
      ),
      (row) => (
        <TextField
          type="text"
          name="explanation"
          size="small"
          disabled={true}
          value={row.explanation ? row.explanation : ""}
          onChange={(e) => handleChange(row, e, "explanation")}
        />
      ),
      (row, index) => {
        const isFromPreauth = nonRemovableExaminationActIds?.size > 0 && row.actId && nonRemovableExaminationActIds.has(String(row.actId));
        return disabled || isFromPreauth ? (<div></div>) : <Button startIcon={<Delete style={{ color: "red" }} />} onClick={() => deleteRow(index)}></Button>;
      }
    ];
  };

  const preHeaders = [
    "\u200b",
    "",
    totalClaimed > 0 ? t("Total Claimed: ") + totalClaimed : "",
    totalTmValue > 0 ? t("TotalTm") + totalTmValue : "",
    totalCounterPriceValue > 0
      ? t("TotalCounted") + totalCounterPriceValue
      : "",
    " ",
    " ",
  ];

  const handleClose = () => {
    setIsPopupOpen(false);
    if (deleteType === 'delete') {
      deleteRow(index);
    } else {
      // Revert to previous rows if popup is triggered and deleteType is not 'delete'
      setRows(prevRows);
      onDataChange(prevRows);
      calculateTotalClaimed(prevRows);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarSeverity("");
  };

  return (
    <StyledPaper>
      <StyledPaperHeader container alignItems="center" direction="row">
        <Grid item alignItems="center">
          <Typography variant="h6">{t("ClaimExaminationServices")}</Typography>
        </Grid>
      </StyledPaperHeader>
      <TableN
        headers={headers()}
        itemFormatters={itemFormatters()}
        preHeaders={preHeaders}
        items={rows}
        withSelection={"single"}
        withPagination={false}
      />
      {!disabled && <Button onClick={addRow}>{t("Add")}</Button>}

      <Dialog open={isPopupOpen} onClose={handleClose}>
        <DialogTitle>{t("Alert")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(popupMessage)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t("OK")}
          </Button>
        </DialogActions>
      </Dialog>

      <CommonSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </StyledPaper>
  );
}

export default ClaimExaminationPannel;
